import React from "react";
import { Form } from "react-crud-form";
import { useNavigate } from "react-router-dom";
import { FormField, FormItem, FormMessage } from "ui/components/form";
import { Input } from "ui/components/input";
import { Checkbox } from "ui/components/checkbox";
import EmployeeInput from "components/employee/input";
import { FormActions } from "components/form/actions";
import { FormLabel } from "components/form";
import TiptapEditor from "components/tiptap-editor";
import { PermissionControlledInput } from "permissions/components/input";
import { useOpeningApplicantForm } from "./useOpeningApplicantForm";
import { applicantSchema } from "./schema";

const ApplicantForm = ({ applicantId, openingId }) => {

    const navigate = useNavigate();

    const { form, handleCreate, handleUpdate, handleRead, requiredFields } = useOpeningApplicantForm({
        applicantId,
        openingId,
    });

    return (
        <Form
            form={form}
            create={handleCreate}
            read={handleRead}
            update={handleUpdate}
            itemId={applicantId || "+"}
            schema={applicantSchema}
        >
            {({
                isCreating,
                createItem,
                isNewItem,
                isUpdating,
                updateItem,
                isDirty,
            }) => (
                <div className="space-y-6">

                    {/* Employee */}
                    <FormField
                        control={form.control}
                        name="employee"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="opening_applicants"
                                    type="form"
                                    field={field.name}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>
                                        Employee
                                    </FormLabel>
                                    <EmployeeInput {...field} />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Experience */}
                    <FormField
                        control={form.control}
                        name="experience"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="opening_applicants"
                                    field={field.name}
                                    type="form"
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>Experience (years)</FormLabel>
                                    <Input
                                        type="number"
                                        value={field.value ?? ""}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            field.onChange(val === "" ? undefined : Number(val));
                                        }}
                                    />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />


                    {/* Resume Link */}
                    <FormField
                        control={form.control}
                        name="resume_url"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create:*" : "update:*"}
                                    collection="opening_applicants"
                                    field={field.name}
                                    type="form"
                                    showWhenDisabled={false}
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>Resume Link</FormLabel>
                                    <Input
                                        type="text"
                                        value={field.value ?? ""}
                                        onChange={(e) =>
                                            field.onChange(e.target.value === "" ? null : e.target.value)
                                        }
                                    />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />


                    {/* Description */}
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create: *" : "update:*"}
                                    collection="opening_applicants"
                                    field={field.name}
                                    type="form"
                                >
                                    <FormLabel required={requiredFields.includes(field.name)}>
                                        Description
                                    </FormLabel>
                                    <TiptapEditor {...field} className="min-h-32" />
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {/* Is Selected */}
                    <FormField
                        control={form.control}
                        name="is_selected"
                        render={({ field }) => (
                            <FormItem>
                                <PermissionControlledInput
                                    rule={isNewItem ? "create: *" : "update:*"}
                                    collection="opening_applicants"
                                    field={field.name}
                                    type="form"
                                >
                                    <div className="flex items-start gap-2">
                                        <Checkbox
                                            id="is_selected"
                                            checked={Boolean(field.value)}
                                            onCheckedChange={field.onChange}
                                        />
                                        <div className="grid gap-2">
                                            <FormLabel required={requiredFields.includes(field.name)}>
                                                Selected
                                            </FormLabel>
                                            <p className="text-muted-foreground text-sm">
                                                Mark this applicant as selected for this opening.
                                            </p>
                                        </div>
                                    </div>
                                </PermissionControlledInput>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormActions
                        isNewItem={isNewItem}
                        isCreating={isCreating}
                        isUpdating={isUpdating}
                        isDirty={isDirty}
                        createItem={() => createItem()}
                        updateItem={() => updateItem().then(() => navigate(-1))}
                        showCancel={false}
                        collection="opening_applicants"
                        rule={isNewItem ? "create:*" : "update:*"}
                    />
                </div>
            )}
        </Form>
    );
};

export default ApplicantForm;
